import { useEffect, useState } from "react";
import { Check, Zap, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";

interface Plan {
  name: string;
  label: string;
  price: number;
  credits: number;
  features: string[];
  popular: boolean;
}

interface Addon {
  credits: number;
  price: number;
}

interface PricingData {
  country: string;
  currency: {
    code: string;
    symbol: string;
  };
  plans: Plan[];
  addons: Addon[];
}

const PricingTeaser = () => {
  const [pricing, setPricing] = useState<PricingData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('get-public-pricing');
      
      if (error) throw error;
      
      setPricing(data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    if (!pricing) return '';
    return `${pricing.currency.symbol}${price.toLocaleString()}`;
  };

  const getPlanIcon = (planName: string) => {
    switch (planName) {
      case 'STARTER':
        return <Zap className="h-6 w-6 text-primary" />;
      case 'PRO_AI':
        return <Sparkles className="h-6 w-6 text-primary" />;
      default:
        return <Check className="h-6 w-6 text-primary" />;
    }
  };

  if (loading) {
    return (
      <section className="py-24 px-4 bg-gradient-to-b from-background to-muted/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <Skeleton className="h-10 w-64 mx-auto" />
            <Skeleton className="h-6 w-96 mx-auto" />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[500px]" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!pricing) return null;

  return (
    <section className="py-24 px-4 bg-gradient-to-b from-background to-muted/20">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Start free, scale as you grow. No hidden fees.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span>Prices in {pricing.currency.code}</span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {pricing.plans.map((plan) => (
            <Card
              key={plan.name}
              className={`relative transition-all hover:shadow-xl ${
                plan.popular ? 'border-primary shadow-lg scale-105' : ''
              }`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                  Most Popular
                </Badge>
              )}
              <CardHeader className="text-center">
                <div className="mx-auto mb-4">
                  {getPlanIcon(plan.name)}
                </div>
                <CardTitle className="text-2xl">{plan.label}</CardTitle>
                <div className="py-4">
                  {plan.price === 0 ? (
                    <div className="text-4xl font-bold">Free Trial</div>
                  ) : (
                    <>
                      <div className="text-4xl font-bold">
                        {formatPrice(plan.price)}
                      </div>
                      <div className="text-muted-foreground">/month</div>
                    </>
                  )}
                </div>
                <CardDescription>
                  {plan.credits} AI Credits {plan.price > 0 && 'per month'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {plan.features.slice(0, 6).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm">
                      <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  variant={plan.popular ? 'default' : 'outline'}
                  onClick={() => window.location.href = '/auth'}
                >
                  Get Started
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Addon Credits */}
        <Card className="max-w-4xl mx-auto bg-gradient-to-r from-primary/5 to-primary/10">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl mb-2">Need More Credits?</CardTitle>
            <CardDescription>
              Purchase addon credit packs that never expire
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {pricing.addons.map((addon) => (
                <div
                  key={addon.credits}
                  className="text-center p-4 bg-background rounded-lg border"
                >
                  <div className="font-semibold text-lg mb-1">
                    {addon.credits} Credits
                  </div>
                  <div className="text-2xl font-bold text-primary">
                    {formatPrice(addon.price)}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center">
              <Button onClick={() => window.location.href = '/pricing'}>
                View Full Pricing Details
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default PricingTeaser;
